import {controlStyle, fieldLabelStyle} from '../lib/formStyles.js';
import Button from '../shared/Button.js';
import {
  computeRuminantBatchTotals,
  modalBackdropStyle,
  modalDialogStyle,
  RuminantHangYield,
  ruminantSavedViewRadioLabelStyle,
} from '../cattle/ruminantPresentation.js';
// SheepBatchesView — hub + router for sheep processing batches.
//
// The lifecycle is schedule → send → in process → complete, the same shape
// cattle has: + Schedule Processing books a dated PLANNED batch, and sheep
// enter it through the send confirmation at the end of a weigh-in session
// (any draft session, any flock per §7), which promotes the batch to ACTIVE.
// Send sheep to processor hands that flow the batch to land in.
//
// The hub lists batches as navigation-only summaries; the record page at
// /sheep/batches/<id> owns editing, per-sheep weights, detach, and delete.
import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {openableProps} from '../shared/openable.js';
import type {OpenableEvent} from '../shared/openable.js';
import {recordSeqNavOptions, labeledSeqItems} from '../lib/recordSequence.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {printRows} from '../lib/printExport.js';
import {buildProcessingBatchExportColumns} from '../lib/operationalExportColumns.js';
import {createSavedView, updateSavedView, deleteSavedView, buildViewState} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
import {
  SHEEP_BATCH_STATUS_KEYS,
  SHEEP_BATCH_SORT_KEYS,
  buildSheepBatchPredicate,
  buildSheepBatchComparator,
} from '../lib/sheepBatchFilters.js';
import SheepBatchPage from './SheepBatchPage.tsx';
import {getProgramColor} from '../lib/programColors.js';
import {
  PROCESSING_STATUS_DISPLAY,
  normalizeProcessingStatus,
  processingStatusLabel,
} from '../lib/processingStatusDisplay.js';
import {StatusChipRow} from '../shared/GroupPillPicker.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
import {sb} from '../lib/supabase.js';
import {nextSheepBatchName} from '../lib/sheepProcessingBatch.js';
import {ensureProcessingFreshness} from '../lib/processingApi.js';
import {fmt} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {SheepBatchesCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import type {SheepBatchCreateVars} from '../app/data/routeMutationCatalog.js';
import {newSheepRequestId} from '../lib/sheepBatchOperationApi.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import Header from '../shared/Header.tsx';
import type {AuthIdentity} from '../contexts/AuthContext.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import type {SheepBatchFilters, SheepBatchSortRule} from '../lib/sheepBatchFilters.js';
import type {ISODateInput} from '../lib/dateUtils.js';

// SheepBatchesHub takes no props of its own; SheepBatchesRouter forwards
// whatever the route element hands it, which today is nothing.
type SheepBatchesProps = Record<string, never>;

// One row of the sheep_detail jsonb. The weights are `number | string` because
// the record page writes parsed numbers while older rows can carry raw text.
type SheepBatchDetailRow = {
  [column: string]: unknown;
  live_weight?: number | string | null;
  hanging_weight?: number | string | null;
};
// A sheep_processing_batches row. The read is `select('*')`, so the row is an
// open record with only the columns this hub names declared, layered onto the
// filter/sort lib's own view of the same row.
type ProcessingBatch = {
  [column: string]: unknown;
  id: string;
  name: string | null;
  status: string | null;
  notes?: string | null;
  created_at?: string | null;
  planned_process_date: string | null;
  actual_process_date: string | null;
  sheep_detail?: SheepBatchDetailRow[] | null;
};
// The augmented row that feeds filter -> sort -> render -> export. The derived
// fields are computed below, not stored.
type AugmentedBatch = ProcessingBatch & {
  animal_count: number;
  total_live_weight: number;
  total_hanging_weight: number;
  yield_pct: number | null;
};
// The persisted filter bag. It is SheepBatchFilters plus whatever key the
// control panel writes, which is why the index signature lives here.
type BatchFilterState = SheepBatchFilters & Record<string, unknown>;
type BatchSortRule = {key: string; dir: 'asc' | 'desc'};
// The new-batch form. Every field is a string because every control is a text
// input, a date input, or a select.
// No `status`: scheduling always creates a PLANNED batch, so there is nothing
// for the form to hold. The row builder writes the literal.
type NewBatchForm = {name: string; planned_process_date: string};
// A saved_views row, reduced to the columns this surface reads. `view_state` is
// jsonb, so its own fields stay `unknown` and the guards below do real
// narrowing rather than restating a claim the type already made.
type SavedViewRow = {
  id: string;
  name: string;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: unknown;
};
type SavedBatchViewState = {filters?: unknown; sortRules?: unknown};
type ErrorLike = {message?: string};

const SHEEP_BATCHES_SURFACE_KEY = 'sheep.batches';
const SHEEP_BATCHES_PARAMS = Object.freeze({});
const EMPTY_SHEEP_BATCHES = Object.freeze([]) as readonly never[];
const EXTENDED_LIST_CONTROLS_ENABLED = false;

// The catalog rethrows PostgREST's `error.message` unchanged, so reading
// `.message` off the rejection reproduces the notice text the direct
// `{error}` envelope produced — including, deliberately, the "undefined" a
// message-less error would have rendered before. This is a conversion, not an
// improvement to the wording.
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as ErrorLike).message) : String(err);

const SHEEP_BATCH_SORT_LABELS: Record<string, string> = {
  batchName: 'Batch name',
  status: 'Status',
  plannedDate: 'Planned date',
  animalCount: 'Sheep count',
  yieldPct: 'Yield %',
};

// Shared column template for the unified Sheep Batches grid. The header row and
// every batch row consume the SAME template so columns stay aligned, Podio-style
// (mirrors PIG_BATCH_GRID_COLUMNS in PigBatchHubTile).
//   Batch | Status | Sheep | Planned | Yield | Open
const SHEEP_BATCH_GRID_COLUMNS = 'minmax(140px, 1.8fr) 96px 64px minmax(120px, 1fr) 80px 60px';

// Exported so the boot-release renderer can mount the hub DIRECTLY for the one
// audited raw route. Going through SheepBatchesRouter would make the released
// index depend on the router's own suffix branch rather than on the release
// boundary, which is the thing being fenced.
export const SheepBatchesHub: React.FC<SheepBatchesProps> = () => {
  const {authState} = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState'> & {
    authState: AuthIdentity | null;
  };
  const navigate = useNavigate();
  // `location.state` is `unknown` by design (src/app/navigation/appLocation.ts):
  // the write side normalized it and no consumer may read it without proving its
  // shape. The state this hub reads is exactly the one SheepBatchPage's delete
  // flow writes, so that proof is applied ONCE, here at the hook call, instead of
  // at the reads — which leaves both reads the same expressions they have always
  // been. The location shape is taken from the facade hook itself, so no facade
  // internal is imported.
  const location = useLocation() as Omit<ReturnType<typeof useLocation>, 'state'> & {
    state?: {notice?: Notice} | null;
  };
  const {useState, useEffect, useMemo} = React;
  const role = authState && authState.role;
  const canEdit = role === 'admin' || role === 'management';

  // The index's only render read belongs to its scope-keyed resource. Saved
  // views intentionally remain a nonblocking preference sidecar below: their
  // failure must never turn a valid batch list into an error state.
  const batchResource = useRouteResource(RESOURCE.SHEEP_BATCHES_CORE, SHEEP_BATCHES_PARAMS);
  const batchData = batchResource.data as SheepBatchesCoreData | null;
  const batches = useMemo<ProcessingBatch[]>(
    () =>
      (batchData ? (batchData.batches as ProcessingBatch[]) : EMPTY_SHEEP_BATCHES).slice().sort((a, b) => {
        const byDate = (x: ProcessingBatch) => x.actual_process_date || x.planned_process_date || x.created_at || '';
        return byDate(b).localeCompare(byDate(a));
      }),
    [batchData],
  );
  const loading = batchResource.loading;
  const loadError: Notice | null = batchResource.error
    ? {
        kind: 'error',
        message:
          'Could not load sheep processing batches. Please refresh the page. (' +
          ((batchResource.error as ErrorLike).message || String(batchResource.error)) +
          ')',
      }
    : null;
  const loadAll = batchResource.refetch;
  // The hub's one write. It carries its own invalidation set — this surface's
  // list, the record page, the sheep home tile and both production summaries —
  // where before it called no refetch whatsoever and simply navigated away.
  // Those readers were not stranded (staleTime 0 refetches them on their next
  // mount), but nothing connected the write to them, so nothing could tell
  // whether the connection was complete. Now the entry names them and the guard
  // derives the list from the read catalog.
  const createBatch = useRouteMutation<SheepBatchCreateVars, null>(MUTATION.SHEEP_BATCH_CREATE);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NewBatchForm | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [creating, setCreating] = useState(false);
  // The name the server suggested when the form opened. Only a name the
  // operator LEFT ALONE may be silently re-allocated on a collision; one they
  // typed is theirs to keep.
  const [suggestedName, setSuggestedName] = useState('');

  // Right-sized list parity: flat filters + a single active sort rule.
  const [filters, setFilters] = useState<BatchFilterState>({});
  const [sortRule, setSortRule] = useState<BatchSortRule>({key: 'plannedDate', dir: 'desc'});
  // R18 status chip; default All, so the list opens exactly as it did.
  const [statusChip, setStatusChip] = useState('all');
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const myProfileId = authState?.user?.id || null;
  const savedViewsQuery = useSavedViewsData(SHEEP_BATCHES_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;

  // Pick up a notice handed off via navigation state (e.g. delete report from
  // SheepBatchPage when blocked detaches need surfacing on the hub).
  useEffect(() => {
    if (location.state && location.state.notice) {
      setNotice(location.state.notice);
      navigate(location.pathname, {replace: true, state: null});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saved views (surface_key sheep.batches). Failure degrades gracefully — a
  // small notice, never blocking the list or its filters.
  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  async function openAdd() {
    if (!canEdit) return;
    setNotice(null);
    // The name comes from migration 206's allocator, which scans every status.
    // The old local scan covered only the batches this view had loaded and could
    // propose a number a completed batch already held; the UNIQUE constraint on
    // name then rejected the save. A failure here is not fatal — the operator can
    // still type a name — so it degrades to an empty field plus a notice.
    let suggested = '';
    try {
      suggested = await nextSheepBatchName(sb);
    } catch (err) {
      setNotice({
        kind: 'warning',
        message: 'Could not suggest a batch name: ' + writeErrorMessage(err) + ' Enter one to continue.',
      });
    }
    setSuggestedName(suggested);
    setForm({
      name: suggested,
      planned_process_date: '',
    });
    setShowForm(true);
  }

  function closeForm() {
    setNotice(null);
    setShowForm(false);
    setForm(null);
    // The save path deliberately holds `creating` across its post-write work, so
    // clearing it belongs here — the one place every exit from the form runs
    // through. Reopening then always starts with an enabled button.
    setCreating(false);
  }

  async function saveNewBatch() {
    if (!canEdit || !form) return;
    setNotice(null);
    const name = (form.name || '').trim();
    if (!name) {
      setNotice({kind: 'error', message: 'Batch name required.'});
      return;
    }
    // A processing date is required to SCHEDULE. Without one the batch exists
    // but gets no Processing record: the reconciler in migration 176 only builds
    // a record for a batch with an actual or planned date
    // (WHERE COALESCE(actual_process_date, planned_process_date) IS NOT NULL),
    // so a dateless batch is invisible on the Processing calendar and has
    // nothing for a cut sheet to hang off. Same rule cattle applies when it
    // schedules a batch.
    const plannedDate = (form.planned_process_date || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(plannedDate)) {
      setNotice({kind: 'error', message: 'Pick a processor date before scheduling this batch.'});
      return;
    }
    setCreating(true);
    const id = String(Date.now()) + Math.random().toString(36).slice(2, 6);
    // MIGRATION 241: the row is built by sheep_batch_operation (batch.create):
    // scheduling always produces a PLANNED batch with no sheep, no totals, no
    // cost and no actual date, so only the id, the name and the planned date
    // are sent. The server also writes the record.created Activity row.
    //
    // The write is the catalog's; the notice, the busy flag and the navigation
    // stay here. `createBatch.run` rejects with the database's own message (a
    // UNIQUE name still reads "duplicate key value violates unique constraint").
    //
    // The error is captured rather than handled inside the catch so that
    // `setCreating(false)` still runs BEFORE the branch, exactly as it did when
    // this was a `{error}` envelope.
    // Scheduling inserts directly rather than through the attach RPC, so it
    // needs its own collision handling: next_sheep_processing_batch_name() only
    // READS the next free number, it does not reserve it, and the suggestion
    // was made when the form opened. Two people scheduling at once, or one
    // leaving the form open, both land on the same taken name and the UNIQUE
    // constraint rejects the insert.
    //
    // Re-allocation only applies to a name the SERVER suggested and the
    // operator left alone. A name they typed themselves is theirs — silently
    // renaming it would be worse than the error — so that case reports the
    // collision and stops.
    let createError: unknown = null;
    let finalName = name;
    // Eligibility is decided ONCE, from what the operator actually submitted.
    // Recomputing it inside the loop compared the REPLACEMENT name against the
    // original suggestion, so a second collision looked like an operator-typed
    // name and stopped retrying.
    const serverSuggested = name === suggestedName && !!suggestedName;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      createError = null;
      try {
        // A new request id per attempt: a re-allocated name is a new intent.
        await createBatch.run({
          requestId: newSheepRequestId(),
          batchId: id,
          name: finalName,
          plannedProcessDate: plannedDate,
        });
        break;
      } catch (err) {
        createError = err;
        const isCollision = /duplicate key|already exists|unique/i.test(writeErrorMessage(err));
        if (!isCollision || !serverSuggested || attempt === 4) break;
        try {
          finalName = await nextSheepBatchName(sb);
        } catch {
          break;
        }
      }
    }
    if (createError) {
      setCreating(false);
      setNotice({kind: 'error', message: 'Create failed: ' + writeErrorMessage(createError)});
      return;
    }
    // Same best-effort freshness nudge the send path makes, for the same
    // reason: a scheduled batch with a date owns a Processing record, and the
    // calendar should not have to wait for the next sweep to show it.
    //
    // `creating` stays TRUE across this await and is never cleared on the happy
    // path — the form closes and the route changes instead. Clearing it first
    // would re-enable "Schedule & open" while the form still holds the name just
    // used, and a second click would hit the UNIQUE constraint on name.
    try {
      await ensureProcessingFreshness(sb, 15);
    } catch (_e) {
      /* the batch is committed; freshness is an optimization */
    }
    closeForm();
    navigate('/sheep/batches/' + id);
  }

  // Send sheep to processor — the action the old empty-shell warning described
  // but never provided. It hands the weigh-in flow the batch to land in; the
  // send itself still happens there, at the chute, where the weights are.
  function sendSheepToProcessor(batch?: {id: string; name?: string | null} | null) {
    navigate('/sheep/weighins', {
      state: batch && batch.id ? {sendToBatch: {id: batch.id, name: batch.name || batch.id}} : {sendToBatch: null},
    });
  }

  // Three states since migration 206. The header's counts line (booked · in
  // process · complete) moved into the R18 status chips below, which count the
  // same three through normalizeProcessingStatus, so an in-process batch still
  // never reads as a future booking.

  // Augment every batch with the per-batch derived fields the export column
  // builder + the filter/sort lib read (animal_count / weights / yield_pct).
  // ONE augmented array feeds filter → sort → render → record-seq nav → export
  // so the visible order and the exported order can never disagree.
  const augmentedBatches = useMemo<AugmentedBatch[]>(
    () =>
      batches.map((batch) => {
        const detailRows = Array.isArray(batch.sheep_detail) ? batch.sheep_detail : [];
        const {totalLive, totalHang} = computeRuminantBatchTotals(detailRows);
        return {
          ...batch,
          animal_count: detailRows.length,
          total_live_weight: totalLive,
          total_hanging_weight: totalHang,
          yield_pct: totalLive > 0 && totalHang > 0 ? (totalHang / totalLive) * 100 : null,
        };
      }),
    [batches],
  );

  const effectiveFilters: BatchFilterState = EXTENDED_LIST_CONTROLS_ENABLED ? filters : {};
  const effectiveSortRule: SheepBatchSortRule = EXTENDED_LIST_CONTROLS_ENABLED
    ? sortRule
    : {key: 'plannedDate', dir: 'desc'};

  const predicateBatches = useMemo(() => {
    const predicate = buildSheepBatchPredicate(effectiveFilters, {});
    return augmentedBatches.filter(predicate);
  }, [augmentedBatches, effectiveFilters]);

  // R18: a status chip row with counts, by the same lifecycle the row badge
  // reads (normalizeProcessingStatus). Default All (Conductor, 2026-10-24:
  // broiler's In Process default is broiler's own). The chip narrows at the
  // filter stage, so the sorted set below stays the one visible-order source;
  // the counts are taken before it, so every chip shows its own total.
  const statusChips = [
    {key: 'all', label: 'All', count: predicateBatches.length},
    ...(
      [
        ['planned', PROCESSING_STATUS_DISPLAY.planned],
        ['in_process', PROCESSING_STATUS_DISPLAY.inProcess],
        ['complete', PROCESSING_STATUS_DISPLAY.complete],
      ] as const
    ).map(([key, label]) => ({
      key,
      label,
      count: predicateBatches.filter((b) => normalizeProcessingStatus(b.status) === key).length,
    })),
  ];
  const filteredBatches = useMemo(
    () =>
      statusChip === 'all'
        ? predicateBatches
        : predicateBatches.filter((b) => normalizeProcessingStatus(b.status) === statusChip),
    [predicateBatches, statusChip],
  );

  const sortedBatches = useMemo(() => {
    const cmp = buildSheepBatchComparator(effectiveSortRule, {});
    return [...filteredBatches].sort(cmp);
  }, [filteredBatches, effectiveSortRule]);

  // The SORTED set is the single source of truth for the visible rows, the
  // record-sequence nav order, and the CSV/print export rows.
  const batchSeqRows = sortedBatches;
  const batchExportRows = sortedBatches;
  const exportColumns = buildProcessingBatchExportColumns({fmt, animalLabel: 'Sheep'});

  const activeFilterCount =
    (Array.isArray(effectiveFilters.status) && effectiveFilters.status.length > 0 ? 1 : 0) +
    (effectiveFilters.plannedDateRange &&
    (effectiveFilters.plannedDateRange.after || effectiveFilters.plannedDateRange.before)
      ? 1
      : 0) +
    (effectiveFilters.animalCountRange &&
    (effectiveFilters.animalCountRange.min != null || effectiveFilters.animalCountRange.max != null)
      ? 1
      : 0) +
    (typeof effectiveFilters.textSearch === 'string' && effectiveFilters.textSearch.trim() ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  function setFilter(key: string, value: unknown) {
    setFilters((prev) => {
      const next = {...prev};
      if (
        value == null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
      ) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }
  function toggleStatusValue(value: string) {
    setFilters((prev) => {
      const cur: string[] = Array.isArray(prev.status) ? (prev.status as string[]) : [];
      const nextValues = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
      const next = {...prev};
      if (nextValues.length === 0) delete next.status;
      else next.status = nextValues;
      return next;
    });
  }
  function setRangeBound(key: string, bound: string, value: string | number) {
    setFilters((prev) => {
      const cur: Record<string, unknown> =
        prev[key] && typeof prev[key] === 'object' ? {...(prev[key] as Record<string, unknown>)} : {};
      if (value === '' || value == null) delete cur[bound];
      else cur[bound] = value;
      const next = {...prev};
      if (!cur.min && cur.min !== 0 && !cur.max && cur.max !== 0 && !cur.after && !cur.before) delete next[key];
      else next[key] = cur;
      return next;
    });
  }
  function clearAllFilters() {
    setFilters({});
  }

  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );

  function sheepBatchesViewState() {
    return buildViewState({filters, sortRules: [{key: sortRule.key, dir: sortRule.dir}], viewMode: 'flat'});
  }
  function applySheepBatchSavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    const st = (view.view_state || {}) as SavedBatchViewState;
    setFilters(st.filters && typeof st.filters === 'object' ? (st.filters as BatchFilterState) : {});
    const rule: SheepBatchSortRule | null =
      Array.isArray(st.sortRules) && st.sortRules[0] ? (st.sortRules[0] as SheepBatchSortRule) : null;
    // `ruleKey` is a narrowing, not new behaviour: SHEEP_BATCH_SORT_KEYS holds
    // strings, so a non-string key answered false before and answers false now.
    const ruleKey = rule && typeof rule.key === 'string' ? rule.key : '';
    if (rule && SHEEP_BATCH_SORT_KEYS.includes(ruleKey)) {
      setSortRule({key: ruleKey, dir: rule.dir === 'desc' ? 'desc' : 'asc'});
    } else {
      setSortRule({key: 'plannedDate', dir: 'desc'});
    }
  }
  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    if (!id) return;
    applySheepBatchSavedView(savedViews.find((v) => v.id === id));
  }
  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setShowSaveViewForm(true);
  }
  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: SHEEP_BATCHES_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: sheepBatchesViewState(),
      })) as {id?: string} | null;
      setShowSaveViewForm(false);
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (caught) {
      const e = caught as ErrorLike;
      setNotice({kind: 'error', message: 'Save view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }
  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: sheepBatchesViewState()});
      await loadSavedViews();
      setNotice({kind: 'success', message: 'Updated "' + selectedView.name + '" to the current filters/sort.'});
    } catch (caught) {
      const e = caught as ErrorLike;
      setNotice({kind: 'error', message: 'Update view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }
  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (caught) {
      const e = caught as ErrorLike;
      setNotice({kind: 'error', message: 'Delete view failed: ' + (e.message || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }
  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    const view = selectedView;
    const run = () => {
      void proceedDeleteSelectedView(view);
    };
    if (window._wcfConfirmDelete) {
      window._wcfConfirmDelete('Delete saved view "' + view.name + '"?', run);
    }
  }

  function handleExportCsv() {
    if (!batchExportRows.length) {
      setNotice({kind: 'warning', message: 'No sheep processing batches to export.'});
      return;
    }
    const ok = downloadCsv(csvFilename('sheep-processing-batches'), rowsToCsv(exportColumns, batchExportRows));
    if (!ok) setNotice({kind: 'warning', message: 'CSV export is unavailable in this browser.'});
  }

  function handlePrintRows() {
    if (!batchExportRows.length) {
      setNotice({kind: 'warning', message: 'No sheep processing batches to print.'});
      return;
    }
    const ok = printRows({
      title: 'Sheep Processing Batches',
      subtitle: batchExportRows.length + ' filtered batches',
      columns: exportColumns,
      rows: batchExportRows,
    });
    if (!ok) setNotice({kind: 'warning', message: 'Print export is unavailable in this browser.'});
  }

  const inpS: React.CSSProperties = controlStyle;
  const lbl: React.CSSProperties = fieldLabelStyle;
  const savedViewRadioLabelS = ruminantSavedViewRadioLabelStyle;

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-sheep-batches-loaded={loading || loadError ? 'false' : 'true'}
    >
      <Header />
      <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
        {!showForm && <InlineNotice notice={loadError} />}
        {!showForm && loadError && (
          <Button
            variant="secondary"
            type="button"
            data-sheep-batches-load-retry="1"
            onClick={loadAll}
            style={{marginBottom: 12}}
          >
            Retry
          </Button>
        )}
        {!showForm && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}
        <div
          style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}
          data-sheep-batches-root
        >
          {/* N09: a Title Case H1. Its old counts line moved into the status
              chips (R18), which carry the same counts. */}
          <h1 className="wcf-page-title">Processing Batches</h1>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end'}}>
            {EXTENDED_LIST_CONTROLS_ENABLED && (
              <>
                <Button variant="secondary" type="button" onClick={handleExportCsv} data-sheep-batches-export-csv="1">
                  Export CSV
                </Button>
                <Button variant="secondary" type="button" onClick={handlePrintRows} data-sheep-batches-print="1">
                  Print
                </Button>
              </>
            )}
            {canEdit && (
              <Button
                variant="secondary"
                type="button"
                onClick={() => sendSheepToProcessor(null)}
                data-sheep-send-to-processor
              >
                Send sheep to processor
              </Button>
            )}
            {canEdit && (
              <Button variant="primary" type="button" onClick={openAdd} data-sheep-schedule-processing>
                + Schedule Processing
              </Button>
            )}
          </div>
        </div>

        {!loading && !loadError && batches.length > 0 && (
          <div style={{marginBottom: 12}}>
            <StatusChipRow
              chips={statusChips}
              selected={statusChip}
              onSelect={setStatusChip}
              rootAttrs={{'data-sheep-batches-status-chips': '1'}}
              summary={
                <span data-sheep-batches-count>
                  Showing {sortedBatches.length} of {batches.length}
                </span>
              }
            />
          </div>
        )}

        {EXTENDED_LIST_CONTROLS_ENABLED && !loadError && !showForm && (
          <>
            {/* Saved views (surface_key sheep.batches) — degrades to a small
                notice on load failure; never blocks the list or filters. */}
            <div
              data-sheep-batches-saved-views-row
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-sheep-batches-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-sheep-batches-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...inpS, width: 'auto', minWidth: 200, fontSize: 12, padding: '6px 10px'}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        variant="secondary"
                        type="button"
                        data-sheep-batches-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-sheep-batches-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  <Button
                    variant="secondary"
                    type="button"
                    data-sheep-batches-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>

            {showSaveViewForm && (
              <div
                data-sheep-batches-saved-view-form
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  data-sheep-batches-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...inpS, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveSheepBatchViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-sheep-batches-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveSheepBatchViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-sheep-batches-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button
                  variant="secondary"
                  type="button"
                  data-sheep-batches-saved-view-save
                  onClick={submitSaveView}
                  disabled={savedViewBusy}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowSaveViewForm(false)}
                  disabled={savedViewBusy}
                >
                  Cancel
                </Button>
              </div>
            )}

            {/* Single toolbar row: search · status · planned-date range ·
                sheep-count range · sort + direction · clear. */}
            <div
              data-sheep-batches-toolbar
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'flex-end',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div style={{minWidth: 180, flex: 1}}>
                <label style={lbl}>Search</label>
                <input
                  type="text"
                  data-sheep-batches-search
                  value={filters.textSearch || ''}
                  placeholder="Batch name or ID"
                  onChange={(e) => setFilter('textSearch', e.target.value)}
                  style={inpS}
                />
              </div>
              <div>
                <label style={lbl}>Status</label>
                <div style={{display: 'flex', gap: 6}}>
                  {SHEEP_BATCH_STATUS_KEYS.map((s) => {
                    const on = Array.isArray(filters.status) && filters.status.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        data-sheep-batches-status-filter={s}
                        onClick={() => toggleStatusValue(s)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 10,
                          border: '1px solid ' + (on ? getProgramColor('sheep') : 'var(--border-strong)'),
                          background: 'var(--surface)',
                          color: on ? getProgramColor('sheep') : 'var(--ink-muted)',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          textTransform: 'capitalize',
                        }}
                      >
                        {processingStatusLabel(s)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label style={lbl}>Planned date</label>
                <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                  <input
                    type="date"
                    data-sheep-batches-planned-after
                    value={(filters.plannedDateRange && filters.plannedDateRange.after) || ''}
                    onChange={(e) => setRangeBound('plannedDateRange', 'after', e.target.value)}
                    style={{...inpS, width: 'auto'}}
                  />
                  <span style={{fontSize: 12, color: 'var(--ink-faint)'}}>to</span>
                  <input
                    type="date"
                    data-sheep-batches-planned-before
                    value={(filters.plannedDateRange && filters.plannedDateRange.before) || ''}
                    onChange={(e) => setRangeBound('plannedDateRange', 'before', e.target.value)}
                    style={{...inpS, width: 'auto'}}
                  />
                </div>
              </div>
              <div>
                <label style={lbl}>Sheep count</label>
                <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                  <input
                    type="number"
                    min="0"
                    data-sheep-batches-count-min
                    value={
                      filters.animalCountRange && filters.animalCountRange.min != null
                        ? filters.animalCountRange.min
                        : ''
                    }
                    placeholder="min"
                    onChange={(e) =>
                      setRangeBound(
                        'animalCountRange',
                        'min',
                        e.target.value === '' ? '' : parseInt(e.target.value, 10),
                      )
                    }
                    style={{...inpS, width: 70}}
                  />
                  <span style={{fontSize: 12, color: 'var(--ink-faint)'}}>to</span>
                  <input
                    type="number"
                    min="0"
                    data-sheep-batches-count-max
                    value={
                      filters.animalCountRange && filters.animalCountRange.max != null
                        ? filters.animalCountRange.max
                        : ''
                    }
                    placeholder="max"
                    onChange={(e) =>
                      setRangeBound(
                        'animalCountRange',
                        'max',
                        e.target.value === '' ? '' : parseInt(e.target.value, 10),
                      )
                    }
                    style={{...inpS, width: 70}}
                  />
                </div>
              </div>
              <div>
                <label style={lbl}>Sort</label>
                <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                  <select
                    data-sheep-batches-sort-key
                    value={sortRule.key}
                    onChange={(e) => setSortRule((prev) => ({...prev, key: e.target.value}))}
                    style={{...inpS, width: 'auto'}}
                  >
                    {SHEEP_BATCH_SORT_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {SHEEP_BATCH_SORT_LABELS[k] || k}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="secondary"
                    type="button"
                    data-sheep-batches-sort-dir
                    onClick={() => setSortRule((prev) => ({...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc'}))}
                    title={sortRule.dir === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    {sortRule.dir === 'asc' ? '↑' : '↓'}
                  </Button>
                </div>
              </div>
              {hasActiveFilters && (
                <Button variant="secondary" type="button" data-sheep-batches-clear-filters onClick={clearAllFilters}>
                  Clear filters ({activeFilterCount})
                </Button>
              )}
            </div>
          </>
        )}

        {loading && <div style={{textAlign: 'center', padding: '2rem', color: 'var(--ink-faint)'}}>Loading{'…'}</div>}

        {!loading && !loadError && batches.length === 0 && (
          <div
            data-sheep-batches-empty
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              padding: '2rem',
              textAlign: 'center',
              color: 'var(--ink-muted)',
              fontSize: 13,
            }}
          >
            No processing batches yet. Click <strong>+ Schedule Processing</strong> to book a date, then{' '}
            <strong>Send sheep to processor</strong> when you weigh them in.
          </div>
        )}

        {!loading && !loadError && batches.length > 0 && sortedBatches.length === 0 && (
          <div
            data-sheep-batches-no-match
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              padding: '2rem',
              textAlign: 'center',
              color: 'var(--ink-muted)',
              fontSize: 13,
            }}
          >
            No sheep processing batches match the current filters.
          </div>
        )}

        {!loading && !loadError && sortedBatches.length > 0 && (
          <div
            data-sheep-batches-grid
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: SHEEP_BATCH_GRID_COLUMNS,
                gap: 10,
                alignItems: 'center',
                padding: '8px 14px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ink-muted)',
              }}
            >
              <span>Batch</span>
              <span style={{justifySelf: 'start'}}>Status</span>
              <span>Sheep</span>
              <span>Processing Date</span>
              <span>Yield</span>
              <span aria-hidden="true" />
            </div>
            {sortedBatches.map((b) => (
              <BatchRow
                key={b.id}
                batch={b}
                fmt={fmt}
                onOpen={() =>
                  navigate('/sheep/batches/' + b.id, recordSeqNavOptions(labeledSeqItems(batchSeqRows, 'name')))
                }
              />
            ))}
          </div>
        )}
      </div>

      {showForm && form && (
        <div
          onClick={closeForm}
          style={{
            ...modalBackdropStyle(),
            alignItems: 'flex-start',
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-sheep-new-batch-modal
            style={{
              ...modalDialogStyle({maxWidth: 480}),
              marginTop: 40,
            }}
          >
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{fontSize: 15, fontWeight: 600, color: 'var(--text-primary)'}}>Schedule Processing</div>
              <button
                type="button"
                onClick={closeForm}
                style={{background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--ink-faint)'}}
              >
                {'×'}
              </button>
            </div>
            <div style={{padding: '16px 20px'}}>
              <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
                <div style={{gridColumn: '1/-1'}}>
                  <label style={lbl}>Name *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    data-sheep-new-batch-name
                    style={inpS}
                  />
                </div>
                {/* No status field. A batch being scheduled is Planned by
                    definition: it cannot be Complete before a single sheep is
                    attached, and In Process is reached by sending sheep, not by
                    picking it here. */}
                <div style={{gridColumn: '1/-1'}}>
                  <label style={lbl}>Processor Date *</label>
                  <input
                    type="date"
                    value={form.planned_process_date}
                    onChange={(e) => setForm({...form, planned_process_date: e.target.value})}
                    data-sheep-new-batch-planned-date
                    style={inpS}
                  />
                </div>
              </div>
              <div
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  fontSize: 11,
                  color: 'var(--ink-muted)',
                }}
              >
                This books the date with the processor. No sheep are attached yet — and flagging a sheep at the chute
                does not attach it either; the send confirmation at the end of a weigh-in session does. When you are
                ready, use <strong>Send sheep to processor</strong> and pick this batch.
              </div>
            </div>
            <div style={{padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8}}>
              <Button
                variant="primary"
                type="button"
                onClick={saveNewBatch}
                disabled={creating}
                data-sheep-new-batch-save
              >
                Schedule & open
              </Button>
              <Button variant="secondary" type="button" onClick={closeForm}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Unified-grid nav-only row for one sheep processing batch (converted from the
// prior stacked card so all visible batches read as one aligned vertical
// inspection table — mirrors PigBatchHubTile). All prior data + the status
// badge + the click-to-open contract + the data attributes are preserved.
type BatchRowProps = {
  batch: AugmentedBatch;
  fmt: (iso: ISODateInput) => string;
  onOpen: (event: OpenableEvent) => void;
};

function BatchRow({batch, fmt, onOpen}: BatchRowProps) {
  const rows = Array.isArray(batch.sheep_detail) ? batch.sheep_detail : [];
  const {yieldPct} = computeRuminantBatchTotals(rows);
  const isComplete = batch.status === 'complete';
  return (
    <div
      data-batch-row={batch.id}
      data-batch-name={batch.name}
      data-batch-status={batch.status}
      {...openableProps(onOpen)}
      className="hoverable-tile"
      style={{
        display: 'grid',
        gridTemplateColumns: SHEEP_BATCH_GRID_COLUMNS,
        gap: 10,
        alignItems: 'center',
        padding: '10px 14px',
        borderBottom: '1px solid var(--divider)',
        cursor: 'pointer',
        background: isComplete ? 'var(--surface-2)' : 'var(--surface)',
      }}
    >
      {/* Batch */}
      <span
        style={{
          fontWeight: 700,
          fontSize: 13,
          color: 'var(--ink)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {batch.name}
      </span>

      {/* Status */}
      <span style={{justifySelf: 'start'}}>
        <Badge status={processingStatusLabel(batch.status)} />
      </span>

      {/* Sheep count */}
      <span style={{fontSize: 12, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums'}}>
        {rows.length > 0 ? <strong>{rows.length}</strong> : '—'}
      </span>

      {/* Planned / processed dates */}
      <span style={{fontSize: 11, color: 'var(--ink-muted)', display: 'flex', gap: 8, flexWrap: 'wrap'}}>
        {/* A lamb batch's planned date IS its booking (mig 206): black date, gray "Booked" (Q14-A). */}
        {batch.planned_process_date ? (
          isComplete ? (
            <span>booked {fmt(batch.planned_process_date)}</span>
          ) : (
            <span data-sheep-batch-booked>
              <strong style={{color: 'var(--ink)', fontWeight: 700}}>{fmt(batch.planned_process_date)}</strong> Booked
            </span>
          )
        ) : (
          <span>—</span>
        )}
        {batch.actual_process_date && (
          <span style={{color: 'var(--ok-ink)'}}>processed {fmt(batch.actual_process_date)}</span>
        )}
      </span>

      {/* Yield */}
      <RuminantHangYield value={yieldPct} showTrack={false} />

      {/* R20/L11: one gray › that turns brand on row hover/focus (the shared
          .chev rule), not an "Open ->" text cell. The whole row opens. */}
      <span className="chev" aria-hidden="true" style={{justifySelf: 'end', fontSize: 18}}>
        ›
      </span>
    </div>
  );
}

function SheepBatchesRouter(props: SheepBatchesProps) {
  const {authState} = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState'> & {
    authState: AuthIdentity | null;
  };
  const location = useLocation();
  const batchDetailId = location.pathname.startsWith('/sheep/batches/')
    ? location.pathname.slice('/sheep/batches/'.length) || null
    : null;
  if (batchDetailId) {
    return React.createElement(SheepBatchPage, {
      sb,
      fmt,
      authState,
    });
  }
  return React.createElement(SheepBatchesHub, props);
}

export default SheepBatchesRouter;
